import { useGetCommerceByIdQuery } from '@/pages/Business/pages/Commerce/services/commerces.service';
import { useGetDriverByIdQuery } from '@/pages/Business/pages/Drivers/services/drivers.service';
import { useCommercesAutocomplete } from '@/pages/Business/pages/Shippings/hooks';
import { useDriversAutocomplete } from '@/pages/Business/pages/Drivers/hooks';
import { useState, useMemo, useCallback } from 'react';
import { resolveSelectedOptionLabel } from '@/utils/selected-option-label';
import { PickUpFilters } from '../models';
import dayjs from 'dayjs';

interface UsePickUpSearchBarProps {
  search: (filters: PickUpFilters) => void;
  initialFilter: PickUpFilters;
  filter: PickUpFilters;
}

export const usePickUpSearchBar = ({
  search,
  initialFilter,
  filter,
}: UsePickUpSearchBarProps) => {
  const [localFilter, setLocalFilter] = useState<PickUpFilters>(filter);
  const [driverInput, setDriverInput] = useState('');
  const [commerceInput, setCommerceInput] = useState('');

  const { driverOptions, isDriversLoading } = useDriversAutocomplete({
    search: driverInput,
    minLength: 3,
  });

  const { commerceOptions, isCommercesLoading } = useCommercesAutocomplete({
    search: commerceInput,
    minLength: 3,
  });

  const { data: driverDetails, isFetching: isDriverDetailsFetching } =
    useGetDriverByIdQuery(
      { id: localFilter.driverId || '' },
      { skip: !localFilter.driverId }
    );

  const selectedDriver = localFilter.driverId
    ? {
        label: resolveSelectedOptionLabel(
          driverDetails
            ? `${driverDetails.firstName} ${driverDetails.lastName}`
            : null,
          {
            isResolving: isDriverDetailsFetching,
            fallback: 'Conductor seleccionado',
          }
        ),
        data: localFilter.driverId,
      }
    : null;

  const { data: commerceDetails, isFetching: isCommerceDetailsFetching } =
    useGetCommerceByIdQuery(
      { commerceId: localFilter.commerceId || '' },
      { skip: !localFilter.commerceId }
    );

  const selectedCommerce = localFilter.commerceId
    ? {
        label: resolveSelectedOptionLabel(commerceDetails?.name, {
          isResolving: isCommerceDetailsFetching,
          fallback: 'Comercio seleccionado',
        }),
        data: localFilter.commerceId,
      }
    : null;

  const resetFilter = useCallback(() => {
    const resetValues = {
      ...initialFilter,
      visitedAtFrom:
        localFilter.visitedAtFrom ?? localFilter.createdAtFrom,
      visitedAtTo: localFilter.visitedAtTo ?? localFilter.createdAtTo,
    };
    setLocalFilter(resetValues);
    setDriverInput('');
    setCommerceInput('');
    search(resetValues);
  }, [
    initialFilter,
    localFilter.createdAtFrom,
    localFilter.createdAtTo,
    localFilter.visitedAtFrom,
    localFilter.visitedAtTo,
    search,
  ]);

  const handleSearch = useCallback(() => {
    const normalizedFilter = {
      ...localFilter,
      createdAtFrom:
        localFilter.createdAtFrom ?? initialFilter.createdAtFrom,
      createdAtTo: localFilter.createdAtTo ?? initialFilter.createdAtTo,
    };
    setLocalFilter(normalizedFilter);
    search(normalizedFilter);
  }, [initialFilter, localFilter, search]);

  const handleDriverChange = useCallback((option: any) => {
    setLocalFilter((prev) => ({
      ...prev,
      driverId: option ? (option.data as string) : undefined,
    }));
  }, []);

  const handleCommerceChange = useCallback((option: any) => {
    setLocalFilter((prev) => ({
      ...prev,
      commerceId: option ? (option.data as string) : undefined,
    }));
  }, []);

  const handleStatusChange = useCallback((statusValue: string) => {
    setLocalFilter((prev) => ({
      ...prev,
      status: statusValue === '' ? undefined : statusValue,
    }));
  }, []);

  const handleCreatedAtFromChange = useCallback((val: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      createdAtFrom: val
        ? dayjs(val).startOf('day').toISOString()
        : undefined,
    }));
  }, []);

  const handleCreatedAtToChange = useCallback((val: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      createdAtTo: val
        ? dayjs(val).endOf('day').toISOString()
        : undefined,
    }));
  }, []);

  const handleVisitedAtFromChange = useCallback((val: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      visitedAtFrom: val
        ? dayjs(val).startOf('day').toISOString()
        : undefined,
    }));
  }, []);

  const handleVisitedAtToChange = useCallback((val: Date | null) => {
    setLocalFilter((prev) => ({
      ...prev,
      visitedAtTo: val
        ? dayjs(val).endOf('day').toISOString()
        : undefined,
    }));
  }, []);

  const createdAtFrom = useMemo(() => {
    return localFilter.createdAtFrom ? new Date(localFilter.createdAtFrom) : null;
  }, [localFilter.createdAtFrom]);

  const createdAtTo = useMemo(() => {
    return localFilter.createdAtTo ? new Date(localFilter.createdAtTo) : null;
  }, [localFilter.createdAtTo]);

  const visitedAtFrom = useMemo(() => {
    return localFilter.visitedAtFrom ? new Date(localFilter.visitedAtFrom) : null;
  }, [localFilter.visitedAtFrom]);

  const visitedAtTo = useMemo(() => {
    return localFilter.visitedAtTo ? new Date(localFilter.visitedAtTo) : null;
  }, [localFilter.visitedAtTo]);

  return {
    localFilter,
    setLocalFilter,
    driverInput,
    setDriverInput,
    commerceInput,
    setCommerceInput,
    isDriversLoading,
    isCommercesLoading,
    selectedDriver,
    selectedCommerce,
    driverOptions,
    commerceOptions,
    resetFilter,
    handleSearch,
    handleDriverChange,
    handleCommerceChange,
    handleStatusChange,
    handleCreatedAtFromChange,
    handleCreatedAtToChange,
    handleVisitedAtFromChange,
    handleVisitedAtToChange,
    createdAtFrom,
    createdAtTo,
    visitedAtFrom,
    visitedAtTo,
  };
};

export default usePickUpSearchBar;
